import { clofoApi } from '@clofo/data/api'
import { ls } from '@clofo/data/local-storage'
import type { AuthToken } from '@clofo/data/types'

/** Statuses that need no further second-factor step. Mirrors the legacy `hasValidTfaStatus`. */
const COMPLETED_TFA_STATUSES: AuthToken['tfa_status'][] = ['disabled', 'verified', 'sso_verified']

export type TfaSession = { status: 'enforced' } | { status: 'enabled'; tfaSecretUri: string }

/**
 * The pending two-factor session left behind by the password step: the obtain token, stored while
 * its tfa_status still requires a second factor. Note the counter-intuitive legacy naming —
 * 'enforced' means the user already has TFA configured and just enters a code, while 'enabled'
 * means they still need to set it up (and therefore needs the token's tfa_secret_uri; without it
 * there is no usable pending session). Any other status means no TFA step is pending.
 */
export const getTfaSession = (): TfaSession | null => {
  const token = ls.getItem('clofoToken')
  if (!token) return null
  if (token.tfa_status === 'enforced') return { status: 'enforced' }
  if (token.tfa_status === 'enabled' && token.tfa_secret_uri) {
    return { status: 'enabled', tfaSecretUri: token.tfa_secret_uri }
  }
  return null
}

export type ExistingSession =
  | { status: 'none' }
  | { status: 'pending-tfa'; path: '/tfa/verify' | '/tfa/confirm' }
  | { status: 'live' }

/**
 * The session a previous login left behind, judged from the stored token alone: `live` once the
 * second factor (if any) is done, `pending-tfa` while a password login still waits on it — with
 * the TFA page that picks it up. Only a token whose refresh half has not yet expired counts, as in
 * the legacy `hasValidRefreshToken`.
 */
export const getExistingSession = (): ExistingSession => {
  const token = ls.getItem('clofoToken')
  if (!token || token.exp_refresh <= Date.now() / 1000) return { status: 'none' }
  if (COMPLETED_TFA_STATUSES.includes(token.tfa_status)) return { status: 'live' }

  // Same rules as the TFA pages' own guard, so the login page never routes to a page that would
  // route straight back.
  const tfaSession = getTfaSession()
  if (!tfaSession) return { status: 'none' }
  return {
    status: 'pending-tfa',
    path: tfaSession.status === 'enforced' ? '/tfa/verify' : '/tfa/confirm',
  }
}

/**
 * Confirms a `live` session with the server before the login page skips ahead to the destination
 * app: the stored token can outlive the refresh cookie (logged out elsewhere, revoked). Resolves to
 * whether the session is still usable; a 401 also clears the stale token. Forced, so the request
 * is made even when the stored token looks fresh (and in dev, where refreshToken() would just
 * return the stored token).
 */
export const confirmExistingSession = async () => {
  try {
    const token = await clofoApi.auth.forceRefreshToken()
    return COMPLETED_TFA_STATUSES.includes(token.tfa_status)
  } catch {
    return false
  }
}
